import type {
  ActiveContract,
  DateRange,
  FlexibilitySuppliers,
  InputsStationIds,
  ListUploadedFiles,
  ReportPayload,
  Site,
  SitePermission,
  SiteReading,
  SiteReport,
  SiteReports,
  SiteUnavailability,
  Station,
  UploadOffersFilePayload,
  UploadOffersFileResponse,
} from '../../types';

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { addYears, endOfYear, format, startOfYear, subYears } from 'date-fns';
import _ from 'lodash';

import { downloadBlob } from '../../utils/download-file';

interface UploadResponse {
  success: boolean;
  message: string;
  fileUrl?: string;
  fileName?: string;
}

interface UploadFilesArgs {
  prms: string;
  siteIds: string;
  client_id: number;
  start_date_from: string;
  start_date_to: string;
  canFetchOffers: boolean;
  canFetchPP: boolean;
  selectedType: string | null;
  page: string | null;
}
interface UploadPayload {
  file: File;
  additionalData?: Record<string, any>;
}

const normalizeIdKeyedMapToArray = <T>(map: T[]): T[] => _.flatten(_.values(map));
export const TEGAMI_URL = 'api/mezzanine/tegami';

/**
 * Site RTK Query API
 *
 * @see https://redux-toolkit.js.org/rtk-query/overview
 */
export const siteApi = createApi({
  reducerPath: 'siteApi',
  baseQuery: fetchBaseQuery({
    headers: {
      'content-type': 'application/json',
    },
  }),
  tagTypes: [
    'SitesReading',
    'Sites',
    'SiteReport',
    'SiteReports',
    'SitesByYear',
    'forCustomer',
    'FlexSupplier',
    'FlexSupplierByUser',
    'ActiveContracts',
    'UploadedFiles',
  ],
  endpoints: (builder) => ({
    fetchAllSites: builder.query<SiteReading[], void>({
      query: () => {
        return {
          url: 'api/mezzanine/v2/sites',
          method: 'GET',
        };
      },
      transformResponse: (response: SiteReading[]) => {
        return response;
      },
      providesTags: ['SitesReading'],
    }),
    fetchStationsFromIds: builder.query<Station[], string>({
      query: (siteIds) => {
        return {
          url: `api/mezzanine/v3/stations?siteIds=${siteIds}`,
          method: 'GET',
        };
      },
      transformResponse: (response: Station[]) => {
        return normalizeIdKeyedMapToArray(response);
      },
    }),
    fetchInputsStationsIds: builder.query<InputsStationIds[], string>({
      query: (stationIds) => {
        return {
          url: `api/mezzanine/v3/inputs?stationIds=${stationIds}`,
          method: 'GET',
        };
      },
      transformResponse: (response: InputsStationIds[]) => {
        return normalizeIdKeyedMapToArray(response);
      },
    }),
    fetchSites: builder.query<Site[], void>({
      query: () => {
        return {
          url: 'api/mezzanine/v2/sites/for-customer',
          method: 'GET',
        };
      },
      transformResponse: (response: Site[]) => {
        return response.map((site) => ({
          ...site,
          siteId: site.siteId.toString(),
          siteName: _.startCase(_.toLower(site.siteName)),
          unscheduledUnavailabilityCutoffHours: site.unscheduledUnavailabilityCutoffHours || 0,
        }));
      },
      providesTags: ['Sites'],
    }),
    fetchSiteReport: builder.query<SiteReport, string>({
      query: (siteId) => {
        return {
          url: `${REPORT_API}/${siteId}`,
          method: 'GET',
        };
      },
      transformResponse: (response: SiteReport, _, siteId) => {
        return {
          ...response,
          siteId: siteId.toString(),
        };
      },
      providesTags: ['SiteReport'],
    }),
    fetchSiteReports: builder.query<SiteReports, SiteReportsArg>({
      query: (args) => {
        return {
          url: `${REPORT_API}/${args.siteIds[0]}`,
          method: 'GET',
          params: {
            start: format(endOfYear(subYears(new Date(args.year, 0, 1), 1)), 'yyyy-MM-dd'),
            end: format(startOfYear(addYears(new Date(args.year, 0, 1), 1)), 'yyyy-MM-dd'),
            additional_site_ids: args.siteIds.join(','),
          },
        };
      },
      providesTags: ['SiteReports'],
    }),
    fetchPerformanceReports: builder.mutation<Response, ReportPayload[]>({
      query: (arg) => {
        const filename = `${[...new Set(arg.map((_) => _.siteId))].join('_')}.zip`;

        return {
          url: 'api/mezzanine/reports/bundle',
          method: 'POST',
          body: {
            fileName: filename,
            reports: arg,
          },
          responseHandler: async (response) => {
            downloadBlob(await response.blob(), filename);

            return response;
          },
          cache: 'no-cache',
        };
      },
    }),
    uploadProductionPlan: builder.mutation<UploadResponse, UploadPayload>({
      query: (payload) => {
        const formData = new FormData();
        formData.append('file', payload.file);

        if (payload.additionalData) {
          Object.entries(payload.additionalData).forEach(([key, value]) => {
            formData.append(key, value);
          });
        }

        return {
          url: `api/henge/production-plan/upload`,
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: {
            'Content-Type': undefined,
          },
        };
      },
      invalidatesTags: ['UploadedFiles'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        try {
          // Wait for the current mutation/query to complete successfully before proceeding
          await queryFulfilled;

          // Get the current Redux state to inspect cached queries
          const state = getState();
          const queries = state.siteApi.queries;

          // Find the most recent 'fetchUploadedFiles' query in the cache
          // We reverse the list to get the latest one first
          const lastFetchQuery = Object.values(queries)
            .reverse()
            .find((q) => q?.endpointName === 'fetchUploadedFiles');

          if (lastFetchQuery) {
            // Retrieve the original arguments used in the last fetchUploadedFiles call
            // so we can re-trigger it with the same parameters
            const fetchArgs = lastFetchQuery.originalArgs as UploadFilesArgs;

            const maxTenta = 3; // Number of times to re-fetch to ensure the file list is up to date

            // Re-fetch the uploaded files list multiple times with a delay between each attempt.
            // This is a polling workaround to handle cases where the server needs a moment
            // to process the upload before the updated file list becomes available.
            for (let i = 0; i < maxTenta; i++) {
              dispatch(siteApi.endpoints.fetchUploadedFiles.initiate(fetchArgs, { forceRefetch: true }));

              // Wait 1.5 seconds between re-fetches (skip the delay after the last iteration)
              if (i < maxTenta - 1) {
                await new Promise((resolve) => setTimeout(resolve, 1500));
              }
            }
          }
        } catch {} // Silently ignore errors to avoid breaking the UI if the re-fetch fails
      },
    }),
    uploadOffersFile: builder.mutation<UploadOffersFileResponse, UploadOffersFilePayload>({
      query: (payload) => {
        const formData = new FormData();
        formData.append('files', payload.file);

        if (payload.additionalData) {
          Object.entries(payload.additionalData).forEach(([key, value]) => {
            formData.append(key, value);
          });
        }

        return {
          url: `${TEGAMI_URL}/offers-capa/files`,
          params: {
            client_id: payload.clientId,
          },
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: {
            'Content-Type': undefined,
          },
        };
      },
      invalidatesTags: ['UploadedFiles'],

      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;

          const state = getState();
          const queries = state.siteApi.queries;

          const lastFetchQuery = Object.values(queries)
            .reverse()
            .find((q) => q?.endpointName === 'fetchUploadedFiles');

          if (lastFetchQuery) {
            const fetchArgs = lastFetchQuery.originalArgs as UploadFilesArgs;
            const maxTenta = 3;
            for (let i = 0; i < maxTenta; i++) {
              dispatch(siteApi.endpoints.fetchUploadedFiles.initiate(fetchArgs, { forceRefetch: true }));
              if (i < maxTenta - 1) {
                await new Promise((resolve) => setTimeout(resolve, 1500)); // 1.5s delay
              }
            }
          }
        } catch {}
      },
    }),
    fetchUnavailabilityPerDate: builder.query<SiteUnavailability[], DateRange>({
      query: (arg) => {
        return {
          url: 'api/henge/unavailability/summary',
          params: {
            from: arg.from,
            to: arg.to,
          },
          method: 'GET',
        };
      },
      transformResponse: (response: SiteUnavailability[]) => {
        return [...response].map((site) => ({
          siteId: site.siteId.toString(),
          availabilityRate: site.availabilityRate,
        }));
      },
    }),
    fetchSingleFlexSupplier: builder.query<FlexibilitySuppliers, { id: number }>({
      query: ({ id }) => {
        return {
          url: `api/mezzanine/v2/flexibility-suppliers/${id}`,
          method: 'GET',
        };
      },
      providesTags: ['FlexSupplier'],
    }),
    fetchSingleFlexSupplierByUser: builder.query<FlexibilitySuppliers, { userId: string }>({
      async queryFn({ userId }, _queryApi, _extraOptions, fetchWithBQ) {
        const permissionsResult = await fetchWithBQ({
          url: 'api/mezzanine/v2/site-permissions',
          method: 'POST',
          body: { userIds: [userId] },
        });

        if (permissionsResult.error) {
          return { error: permissionsResult.error };
        }

        const supplierId = (permissionsResult.data as Array<SitePermission>)[0].flexibilitySupplierId;

        const supplierResult = await fetchWithBQ({
          url: `api/mezzanine/v2/flexibility-suppliers/${supplierId}`,
          method: 'GET',
        });

        if (supplierResult.error) {
          return { error: supplierResult.error };
        }
        return { data: supplierResult.data as FlexibilitySuppliers };
      },
      providesTags: ['FlexSupplierByUser'],
    }),
    fetchUploadedFiles: builder.query<ListUploadedFiles, UploadFilesArgs>({
      async queryFn(
        { prms, siteIds, client_id, start_date_from, start_date_to, canFetchOffers, canFetchPP, selectedType, page },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const shouldFetchOffers = canFetchOffers && (selectedType === null || selectedType === 'offers');
        const shouldFetchPP = canFetchPP && (selectedType === null || selectedType === 'production-plan');

        const pageSize = 20;
        const currentPage = parseInt(page || '1', 10);

        if (shouldFetchOffers && shouldFetchPP) {
          const itemsPerSource = pageSize / 2;
          const offsetPerSource = (currentPage - 1) * itemsPerSource;

          const [offersResult, ppResult] = await Promise.allSettled([
            fetchWithBQ({
              url: `${TEGAMI_URL}/offers-capa/uploaded-files`,
              params: {
                prms,
                client_id,
                start_date_from,
                start_date_to,
                limit: itemsPerSource,
                offset: offsetPerSource,
              },
              method: 'GET',
              cache: 'no-store',
            }) as Promise<{ data: ListUploadedFiles; error?: { message: string } }>,
            fetchWithBQ({
              url: `api/henge/production-plan`,
              params: {
                ...(siteIds && { siteIds }),
                start_date_from,
                start_date_to,
                limit: itemsPerSource,
                offset: offsetPerSource,
              },
              method: 'GET',
              cache: 'no-store',
            }) as Promise<{ data: ListUploadedFiles; error?: { message: string } }>,
          ]);
          const offersData = offersResult.status === 'fulfilled' && !offersResult.value.error ? offersResult.value.data : null;
          const offersError =
            offersResult.status === 'fulfilled' && offersResult.value.error
              ? offersResult.value.error
              : offersResult.status === 'rejected'
              ? offersResult.reason
              : null;

          const ppData = ppResult.status === 'fulfilled' && !ppResult.value.error ? ppResult.value.data : null;
          const ppError =
            ppResult.status === 'fulfilled' && ppResult.value.error ? ppResult.value.error : ppResult.status === 'rejected' ? ppResult.reason : null;

          if (!offersData && !ppData) {
            throw {
              message: 'both',
            };
          }

          const allItems = [...(offersData?.items || []), ...(ppData?.items || [])];

          // Sort by upload date (the server already do this, but since we fetch from both services we HAVE to do it in the front too)
          const sortedItems = allItems.sort((a, b) => new Date(b.uploadTimestamp).getTime() - new Date(a.uploadTimestamp).getTime());

          const result: ListUploadedFiles & { error: { message: string } | null } = {
            total: (offersData?.total || 0) + (ppData?.total || 0),
            limit: pageSize,
            offset: (currentPage - 1) * pageSize,
            items: sortedItems,

            error: null,
          };

          if (offersError || ppError) {
            result.error = { message: offersError ? 'offers' : 'production-plan' };
          }

          return { data: result };
        } else {
          const offset = (currentPage - 1) * pageSize;

          const result = shouldFetchOffers
            ? await fetchWithBQ({
                url: `${TEGAMI_URL}/offers-capa/uploaded-files`,
                params: { prms, client_id, start_date_from, start_date_to, limit: pageSize, offset },
                method: 'GET',
                cache: 'no-store',
              })
            : await fetchWithBQ({
                url: `api/henge/production-plan`,
                params: { ...(siteIds && { siteIds }), start_date_from, start_date_to, limit: pageSize, offset },
                method: 'GET',
                cache: 'no-store',
              });

          if (result.error) throw { message: shouldFetchOffers ? 'offers' : 'production-plan' };
          return { data: result.data as ListUploadedFiles };
        }
      },
      providesTags: ['UploadedFiles'],
    }),
    fetchActiveContracts: builder.query<ActiveContract, { siteIds: string }>({
      query: (arg) => ({
        url: `api/mezzanine/v2/contracts?siteIds=${arg.siteIds}&active=true`,
        method: 'GET',
      }),
      providesTags: ['ActiveContracts'],
    }),
  }),
});

const REPORT_API = `${TEGAMI_URL}/reports`;
export const REPORT_PDF = `${REPORT_API}/pdf/`;

type SiteReportsArg = {
  siteIds: string[];
  year: number;
};

export const {
  useFetchAllSitesQuery,
  useLazyFetchAllSitesQuery,
  useLazyFetchInputsStationsIdsQuery,
  useFetchSitesQuery,
  useLazyFetchSitesQuery,
  useFetchSiteReportQuery,
  useLazyFetchSiteReportQuery,
  useFetchSiteReportsQuery,
  useLazyFetchSiteReportsQuery,
  useFetchPerformanceReportsMutation,
  useFetchUnavailabilityPerDateQuery,
  useLazyFetchUnavailabilityPerDateQuery,
  //TODO this should go to station service
  useLazyFetchStationsFromIdsQuery,
  useFetchSingleFlexSupplierQuery,
  useUploadOffersFileMutation,
  useFetchActiveContractsQuery,
  useLazyFetchActiveContractsQuery,
  useFetchSingleFlexSupplierByUserQuery,
  useUploadProductionPlanMutation,
  useFetchUploadedFilesQuery,
  useLazyFetchUploadedFilesQuery,
} = siteApi;
